/**
 * SkipTheDealerships.ai — Payment Calculation Engine
 *
 * All monetary values in CAD cents to avoid floating-point drift.
 * Rates stored as decimals (e.g. 7.99 = 7.99%).
 * Canadian auto loans use monthly compounding (not semi-annual like mortgages).
 */

export const FREQUENCIES = {
  monthly: { label: 'Monthly', periodsPerYear: 12 },
  biweekly: { label: 'Bi-weekly', periodsPerYear: 26 },
};

export const CREDIT_TIERS = [
  { value: 'A+', label: 'A+ (750+)' },
  { value: 'A', label: 'A  (700–749)' },
  { value: 'B', label: 'B  (650–699)' },
  { value: 'C', label: 'C  (600–649)' },
  { value: 'D', label: 'D  (550–599)' },
];

export const VEHICLE_CATEGORIES = [
  { value: 'automotive', label: 'Automotive' },
  { value: 'rv', label: 'RV / Motorhome' },
  { value: 'marine', label: 'Marine / Watercraft' },
  { value: 'powersports', label: 'Powersports' },
  { value: 'trailer', label: 'Recreational Trailer' },
];

export const PROVINCES = [
  { value: 'AB', label: 'Alberta', taxRate: 0.05 },
  { value: 'BC', label: 'British Columbia', taxRate: 0.12 },
  { value: 'MB', label: 'Manitoba', taxRate: 0.12 },
  { value: 'NB', label: 'New Brunswick', taxRate: 0.15 },
  { value: 'NL', label: 'Newfoundland', taxRate: 0.15 },
  { value: 'NS', label: 'Nova Scotia', taxRate: 0.15 },
  { value: 'ON', label: 'Ontario', taxRate: 0.13 },
  { value: 'PE', label: 'PEI', taxRate: 0.15 },
  { value: 'QC', label: 'Quebec', taxRate: 0.14975 },
  { value: 'SK', label: 'Saskatchewan', taxRate: 0.11 },
  { value: 'NT', label: 'Northwest Territories', taxRate: 0.05 },
  { value: 'NU', label: 'Nunavut', taxRate: 0.05 },
  { value: 'YT', label: 'Yukon', taxRate: 0.05 },
];

// GAP coverage — tiered by vehicle price
export const GAP_COST_STANDARD = 2348; // vehicles under $40,000
export const GAP_COST_PREMIUM = 3478; // vehicles $40,000 and above
export const GAP_PRICE_TIER = 40000; // threshold between tiers
export const GAP_THRESHOLD = 45000; // max financed amount eligible for GAP

// Warranty cost — tiered by vehicle price (subject to approval)
export const WARRANTY_COST_STANDARD = 1995; // vehicles under $40,000
export const WARRANTY_COST_PREMIUM = 3999; // vehicles $40,000 and above

// Warranty eligibility thresholds
export const WARRANTY_MAX_AGE_YEARS = 3;
export const WARRANTY_MAX_KM = 60000;

// ─── Core amortization formula ────────────────────────────────────────────────

/**
 * Periodic payment using standard amortization.
 * Uses equivalent-rate conversion so biweekly/weekly payments are actuarially
 * correct relative to the monthly compounding base.
 *
 * @param {number} principal   Amount financed (CAD)
 * @param {number} annualRate  Annual interest rate (percent, e.g. 7.99)
 * @param {number} termMonths  Loan term in months
 * @param {string} frequency   Key from FREQUENCIES
 * @returns {{ payment, totalCost, totalInterest, numPeriods }}
 */
export function calculatePayment(principal, annualRate, termMonths, frequency = 'monthly') {
  if (principal <= 0 || termMonths <= 0) {
    return { payment: 0, totalCost: 0, totalInterest: 0, numPeriods: 0 };
  }

  const { periodsPerYear } = FREQUENCIES[frequency];

  if (annualRate === 0) {
    const numPeriods = Math.round((termMonths * periodsPerYear) / 12);
    const payment = principal / numPeriods;
    return { payment, totalCost: principal, totalInterest: 0, numPeriods };
  }

  // Monthly rate from annual
  const monthlyRate = annualRate / 100 / 12;

  // Equivalent periodic rate (monthly compounding base)
  const periodicRate = Math.pow(1 + monthlyRate, 12 / periodsPerYear) - 1;

  const numPeriods = Math.round((termMonths * periodsPerYear) / 12);

  // Standard amortization
  const payment = (principal * periodicRate) / (1 - Math.pow(1 + periodicRate, -numPeriods));

  const totalCost = payment * numPeriods;
  const totalInterest = totalCost - principal;

  return { payment, totalCost, totalInterest, numPeriods };
}

// ─── Amount financed ──────────────────────────────────────────────────────────

/**
 * Calculate the total amount financed after trade, down payment, tax and fees.
 */
export function calculateAmountFinanced({
  vehiclePrice,
  tradeValue = 0,
  tradePayout = 0, // amount still owed on trade
  downPayment = 0,
  province = 'MB',
  sellerType, // undefined -- see getApplicableTaxRate below
  fees = 0, // documentation, registration, etc.
  applyTaxOnFull = true, // some provinces tax on full price before trade
}) {
  const tradeEquity = tradeValue - tradePayout;
  // `sellerType` is optional so existing callers (PaymentCalculator's
  // generic estimator, which has no notion of seller type) keep their prior
  // registrant-rate behavior unchanged.
  const taxRate =
    sellerType !== undefined
      ? getApplicableTaxRate(province, sellerType)
      : (PROVINCES.find(p => p.value === province) || PROVINCES.find(p => p.value === 'MB'))
          .taxRate;

  // Tax applies to vehicle price (some provinces reduce taxable amount by trade equity)
  const taxableAmount = applyTaxOnFull
    ? vehiclePrice
    : Math.max(0, vehiclePrice - Math.max(0, tradeEquity));
  const tax = taxableAmount * taxRate;

  // Positive trade equity reduces the amount financed; negative equity (owing
  // more on the trade than it's worth) increases it -- that deficit doesn't
  // vanish, it rolls into the new loan. Clamping it to 0 here would silently
  // drop it instead.
  const amountFinanced = vehiclePrice + tax + fees - downPayment - tradeEquity;
  return Math.max(0, amountFinanced);
}

// ─── Warranty / GAP eligibility ───────────────────────────────────────────────

export function isGapEligible(baseAmountFinanced) {
  return baseAmountFinanced < GAP_THRESHOLD;
}

// Returns the correct GAP cost based on vehicle price tier
export function getGapCost(vehiclePrice) {
  return vehiclePrice >= GAP_PRICE_TIER ? GAP_COST_PREMIUM : GAP_COST_STANDARD;
}

// Returns the default warranty cost based on vehicle price tier
export function getWarrantyCost(vehiclePrice) {
  return vehiclePrice >= GAP_PRICE_TIER ? WARRANTY_COST_PREMIUM : WARRANTY_COST_STANDARD;
}

/**
 * Warranty eligibility based on in-service date (first sale date from VIN decode).
 * @param {string|Date|null} inServiceDate  ISO date string or Date object
 * @param {number}           vehicleKm
 */
export function isWarrantyEligible(inServiceDate, vehicleKm) {
  if (!inServiceDate) return false; // unknown in-service date → not eligible
  const inService = new Date(inServiceDate);
  if (isNaN(inService.getTime())) return false;
  const ageYears = (Date.now() - inService.getTime()) / (365.25 * 24 * 3600 * 1000);
  return ageYears < WARRANTY_MAX_AGE_YEARS && vehicleKm < WARRANTY_MAX_KM;
}

// ─── Lender eligibility ───────────────────────────────────────────────────────

/**
 * Returns all programs from lenderRates that this deal qualifies for,
 * with the best rate+term picked per lender.
 *
 * @param {object} deal   Deal parameters
 * @param {array}  rates  Lender rate table (from lenderRates.json)
 * @returns {array} Eligible programs sorted by rate ASC, then term DESC
 */
export function findEligiblePrograms(deal, rates) {
  const { category, vehicleYear, vehicleKm, amountFinanced, creditTier, province } = deal;

  const today = new Date();
  const currentYear = new Date().getFullYear();
  const vehicleAge = currentYear - vehicleYear;

  const eligible = [];

  for (const lender of rates) {
    if (lender.category !== category) continue;
    if (!lender.active) continue;

    for (const program of lender.programs || []) {
      // Province check
      if (program.provinces?.length && !program.provinces.includes(province)) continue;

      // Credit tier check
      if (program.creditTiers?.length && !program.creditTiers.includes(creditTier)) continue;

      // Amount check
      if (program.minAmount && amountFinanced < program.minAmount) continue;
      if (program.maxAmount && amountFinanced > program.maxAmount) continue;

      // Vehicle age check
      if (program.maxVehicleAgeYears !== undefined && vehicleAge > program.maxVehicleAgeYears)
        continue;
      if (program.minVehicleYear && vehicleYear < program.minVehicleYear) continue;

      // Km check
      if (program.maxKm !== undefined && vehicleKm > program.maxKm) continue;

      // Find eligible rate tiers within this program
      const eligibleRates = (program.rates || []).filter(rt => {
        if (rt.disabled) return false;
        if (rt.expiryDate && new Date(rt.expiryDate) < today) return false;
        if (rt.effectiveDate && new Date(rt.effectiveDate) > today) return false;
        // Per-rate vehicle year range (e.g. CIBC different max terms per MY)
        if (rt.minVehicleYear && vehicleYear < rt.minVehicleYear) return false;
        if (rt.maxVehicleYear && vehicleYear > rt.maxVehicleYear) return false;
        return true;
      });

      if (!eligibleRates.length) continue;

      // Pick lowest rate; at same rate, longest term
      const best = eligibleRates.reduce((prev, curr) => {
        if (curr.rate < prev.rate) return curr;
        if (curr.rate === prev.rate && curr.termMonths > prev.termMonths) return curr;
        return prev;
      });

      eligible.push({
        lenderId: lender.id,
        lenderName: lender.name,
        programName: program.name,
        rate: best.rate,
        termMonths: best.termMonths,
        rateId: best.id,
        // Audit trail
        _audit: {
          lenderId: lender.id,
          programId: program.id,
          rateId: best.id,
          effectiveDate: best.effectiveDate,
          evaluatedAt: today.toISOString(),
          dealSnapshot: { category, vehicleYear, vehicleKm, amountFinanced, creditTier, province },
        },
      });
    }
  }

  // Sort: lowest rate first, then longest term
  return eligible.sort((a, b) => a.rate - b.rate || b.termMonths - a.termMonths);
}

/**
 * Same eligibility filtering as findEligiblePrograms, but looks for the
 * lowest rate at ONE specific term instead of collapsing across all terms
 * to a single "best" pick. Real lender/dealer rate sheets vary by term
 * (longer term = higher rate), so a fixed-term-card display needs a rate
 * per term, not one flat rate applied to every card.
 *
 * @returns {number|null} the lowest eligible rate at that term, or null if
 *   no lender/program has an eligible rate for that exact term.
 */
export function findRateForTerm(deal, rates, termMonths) {
  const { category, vehicleYear, vehicleKm, amountFinanced, creditTier, province } = deal;
  const today = new Date();
  const currentYear = new Date().getFullYear();
  const vehicleAge = currentYear - vehicleYear;

  let best = null;

  for (const lender of rates || []) {
    if (lender.category !== category) continue;
    if (!lender.active) continue;

    for (const program of lender.programs || []) {
      if (program.provinces?.length && !program.provinces.includes(province)) continue;
      if (program.creditTiers?.length && !program.creditTiers.includes(creditTier)) continue;
      if (program.minAmount && amountFinanced < program.minAmount) continue;
      if (program.maxAmount && amountFinanced > program.maxAmount) continue;
      if (program.maxVehicleAgeYears !== undefined && vehicleAge > program.maxVehicleAgeYears)
        continue;
      if (program.minVehicleYear && vehicleYear < program.minVehicleYear) continue;
      if (program.maxKm !== undefined && vehicleKm > program.maxKm) continue;

      for (const rt of program.rates || []) {
        if (rt.termMonths !== termMonths) continue;
        if (rt.disabled) continue;
        if (rt.expiryDate && new Date(rt.expiryDate) < today) continue;
        if (rt.effectiveDate && new Date(rt.effectiveDate) > today) continue;
        if (rt.minVehicleYear && vehicleYear < rt.minVehicleYear) continue;
        if (rt.maxVehicleYear && vehicleYear > rt.maxVehicleYear) continue;
        if (best === null || rt.rate < best) best = rt.rate;
      }
    }
  }

  return best;
}

// Reference dealer-rate assumptions, term-varying (longer term = higher
// rate), sourced directly from a dealer payment-assumptions reference
// provided for this feature -- not invented. Used ONLY as the fallback when
// no real lender program has an eligible rate for that specific term (e.g.
// lenderRates hasn't loaded yet, or nothing matches this deal); always
// clearly labeled in the UI as an estimate, never presented as a live quote
// or as Skip's own rate.
export const REFERENCE_TERM_RATES = {
  60: 6.99,
  72: 7.39,
  84: 7.99,
  96: 8.49,
};

// ─── Full quote builder ───────────────────────────────────────────────────────

/**
 * Build a complete quote including standard and protected loan payments.
 */
export function buildQuote({ deal, program, frequency, warrantyAmount = 0 }) {
  const { amountFinanced } = deal;
  const { rate, termMonths } = program;

  // Standard payment
  const standard = calculatePayment(amountFinanced, rate, termMonths, frequency);

  // Protected loan
  const gapEligible = isGapEligible(amountFinanced);
  const warrantyEligible = isWarrantyEligible(deal.vehicleYear, deal.vehicleKm);
  const gapCost = getGapCost(deal.vehiclePrice || amountFinanced);

  let protectedAmount = amountFinanced;
  const addons = [];

  if (gapEligible) {
    protectedAmount += gapCost;
    addons.push({ name: 'GAP Protection', cost: gapCost, subjectToApproval: true });
  }
  if (warrantyEligible && warrantyAmount > 0) {
    protectedAmount += warrantyAmount;
    addons.push({ name: 'Wrap Warranty', cost: warrantyAmount, subjectToApproval: true });
  }

  const protected_ = calculatePayment(protectedAmount, rate, termMonths, frequency);
  const paymentDiff = protected_.payment - standard.payment;

  return {
    lenderName: program.lenderName,
    programName: program.programName,
    rate,
    termMonths,
    amountFinanced,
    frequency,
    standard: {
      payment: standard.payment,
      totalCost: standard.totalCost,
      totalInterest: standard.totalInterest,
      numPeriods: standard.numPeriods,
    },
    protected: {
      amountFinanced: protectedAmount,
      addons,
      gapEligible,
      warrantyEligible,
      payment: protected_.payment,
      totalCost: protected_.totalCost,
      totalInterest: protected_.totalInterest,
      numPeriods: protected_.numPeriods,
      paymentDiff,
    },
    audit: program._audit,
  };
}

// ─── Formatting helpers ───────────────────────────────────────────────────────

export function formatCAD(amount) {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
  }).format(amount);
}

export function formatRate(rate) {
  return `${rate.toFixed(2)}%`;
}

// ─── Purchase Options: seller type & tax path ─────────────────────────────────
//
// GST/HST/PST applies when a specified motor vehicle is bought from a GST/HST
// registrant such as a dealership -- canada.ca/en/revenue-agency/services/tax/
// businesses/topics/gst-hst-businesses/charge-collect-specific-situations/
// motor-vehicles.html (accessed 2026-08-02). A private-party sale is a
// different tax event: no GST/HST is collected by the seller at all: the
// buyer pays a province-specific tax directly to the registration authority
// when they register the vehicle, and Skip is not the collector of record
// for it either way. `PROVINCES[].taxRate` above already models the
// registrant/dealer rate; `PRIVATE_SALE_TAX_RATES` below is the separate,
// lower-in-some-provinces rate paid at registration on a private sale.

export const SELLER_TYPES = {
  private: 'private',
  dealer: 'dealer',
  broker: 'broker',
  business: 'business',
  wholesaler: 'wholesaler',
};

const REGISTRANT_SELLER_TYPES = [
  SELLER_TYPES.dealer,
  SELLER_TYPES.broker,
  SELLER_TYPES.business,
  SELLER_TYPES.wholesaler,
];

export const isRegistrantSeller = sellerType => REGISTRANT_SELLER_TYPES.includes(sellerType);

// Private-sale registration tax by province. Sourced 2026-08-02:
// - ON: 13% RST at registration (ServiceOntario) -- carrookie.ca/articles/tax-on-a-car-in-ontario
// - BC: 12% PST at registration for passenger vehicles under $55,000 (tiers
//   above that threshold are not modeled here) -- icbc.com/brochures/pst-on-vehicles
// - MB: 7% RST, no GST on a private sale -- mbautoinsurance.ca/used-car-tax-calculator
// - SK: 6% PST, no GST on a private sale (a $5,000-and-under exemption exists
//   and is not modeled here) -- cartax.online/saskatchewan-pst-vehicles
// - QC: 9.975% QST at SAAQ registration, no GST -- legalclarity.org/quebec-car-sales-tax-rates-for-new-and-used-cars
// - NS: 14% provincial tax at registration -- cbc.ca/lite/story/1.7644789
// - NB, PE, NL: 15% Provincial Vehicle Tax on private sales -- gnb.ca (New
//   Brunswick Department of Finance, "Taxation of Vehicles")
// - AB, NT, NU, YT: no PST/RST of any kind -- savvynewcanadians.com/car-sales-tax-canada
export const PRIVATE_SALE_TAX_RATES = {
  AB: 0,
  BC: 0.12,
  MB: 0.07,
  NB: 0.15,
  NL: 0.15,
  NS: 0.14,
  ON: 0.13,
  PE: 0.15,
  QC: 0.09975,
  SK: 0.06,
  NT: 0,
  NU: 0,
  YT: 0,
};

/**
 * The tax rate that applies to this deal, based on who's selling.
 * Registrant sellers (dealer/broker/business/wholesaler) collect/remit
 * GST/HST/PST like any other registrant sale. A private sale isn't collected
 * by the seller (or by Skip) at all -- it's paid by the buyer directly to
 * the province at registration, at that province's private-sale rate.
 */
export function getApplicableTaxRate(province, sellerType) {
  const table = isRegistrantSeller(sellerType) ? PROVINCES : null;
  if (table) {
    const provinceData = table.find(p => p.value === province);
    return provinceData ? provinceData.taxRate : 0;
  }
  return PRIVATE_SALE_TAX_RATES[province] ?? 0;
}

// ─── Purchase Options: protection products ────────────────────────────────────
//
// Flat, term-of-ownership add-on pricing. Order-of-magnitude grounded in
// published 2026 F&I/aftermarket pricing (not a live product catalog --
// same "estimate, not a quote" posture as the delivery-estimate endpoint):
// - Roadside assistance: standalone/membership plans commonly run
//   $50-150/year, dealer F&I roadside add-ons are typically sold as a single
//   flat price for the loan term -- owl-let.com/blog/how-much-does-roadside-assistance-cost,
//   latestcost.com/average-cost-of-roadside-assistance
// - Road hazard / tire & wheel protection: multi-year dealer tire & wheel
//   plans commonly carry per-tire caps in the $200-400 range with a
//   plan-wide cap around $1,200 -- fidelitywarrantyservices.com/products/road-hazard-protection,
//   nissanusa.com (Tire & Wheel Road Hazard Protection Plan)
export const ROADSIDE_ASSISTANCE_COST = 149;
export const ROAD_HAZARD_TIRE_COST = 595;

// Vehicle replacement protection -- flat price per product spec.
export const VEHICLE_REPLACEMENT_COST = 1800;
// "if configured" -- a single flag so this product can be turned off without
// touching FinanceTab/CashTab rendering logic.
export const VEHICLE_REPLACEMENT_ENABLED = true;

// Registration/transfer government fee estimate. Real transfer fees run
// roughly $15-32 in government charges alone (Ontario title transfer $32,
// Alberta ownership transfer $15-25) plus a registry-agent service fee in
// some provinces -- rinregister.ca/vehicle-registration-ontario-cost-2025-complete-fee-guide,
// shifthappensautosales.ca/blog/alberta-vehicle-registration-fees-explained-2026.
// This is a single flat estimate across provinces, not a per-province table:
// the range is narrow enough, and the amount small enough relative to the
// other landed-cost line items, that per-province precision isn't worth the
// added surface here -- it is still clearly labeled as an estimate.
export const REGISTRATION_TRANSFER_ESTIMATE = 65;

// ─── Purchase Options: cash-purchase landed cost ──────────────────────────────

/**
 * Total estimated out-the-door cost for a cash purchase: price, tax
 * (registrant or private-sale rate, whichever applies), delivery, and the
 * registration/transfer estimate. Optional protection products are
 * deliberately NOT included here -- they're shown as separate line items on
 * the Cash tab, not summed into the headline landed-cost number.
 */
export function calculateCashLandedCost({
  vehiclePrice,
  province,
  sellerType,
  deliveryEstimate = 0,
  registrationEstimate = REGISTRATION_TRANSFER_ESTIMATE,
}) {
  const taxRate = getApplicableTaxRate(province, sellerType);
  const tax = vehiclePrice * taxRate;
  const total = vehiclePrice + tax + deliveryEstimate + registrationEstimate;
  return {
    vehiclePrice,
    tax,
    taxRate,
    deliveryEstimate,
    registrationEstimate,
    total,
  };
}

// ─── Purchase Options: negative equity ─────────────────────────────────────────

/**
 * A dealership's own calculator typically only shows the trade value it's
 * offering -- if the buyer owes more on the trade than that (negative
 * equity), that deficit doesn't disappear, it gets rolled into the new
 * loan's amount financed (see calculateAmountFinanced above, which no
 * longer clamps it away). This surfaces that deficit and its payment impact
 * on their own, before the buyer ever sees a dealership's number.
 */
export function calculateNegativeEquityImpact({
  tradeValue = 0,
  tradePayout = 0,
  rate,
  termMonths,
  frequency = 'monthly',
}) {
  const negativeEquity = Math.max(0, tradePayout - tradeValue);
  const paymentImpact =
    negativeEquity > 0 ? calculatePayment(negativeEquity, rate, termMonths, frequency).payment : 0;
  return { negativeEquity, paymentImpact };
}

// ─── Purchase Options: fixed-term payment cards ───────────────────────────────

const TERM_CARD_MONTHS = [60, 72, 84, 96];

/**
 * One payment quote per term, all at the same program (APR) -- mirrors how a
 * single "best eligible program" is already picked elsewhere in this file;
 * this just varies the term across cards instead of picking one term.
 */
export function buildTermQuotes({
  amountFinanced,
  rate,
  frequency = 'monthly',
  terms = TERM_CARD_MONTHS,
}) {
  return terms.map(termMonths => ({
    termMonths,
    rate,
    frequency,
    ...calculatePayment(amountFinanced, rate, termMonths, frequency),
  }));
}
